// src/components/superadmin/NotificationCenter.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  BellIcon, 
  CheckIcon, 
  CreditCardIcon, 
  RocketLaunchIcon, 
  BanknotesIcon, 
  TicketIcon, 
  CheckCircleIcon,
  XMarkIcon,
  InformationCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { 
  AppNotification, 
  subscribeToSuperAdminNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead 
} from '../../services/notificationService';

export const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread' | 'payments' | 'tickets'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Suscribirse a las notificaciones en tiempo real
  useEffect(() => {
    const unsubscribe = subscribeToSuperAdminNotifications((notifs) => {
      setNotifications(notifs);
    }, 40);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const [selectedNotifForModal, setSelectedNotifForModal] = useState<AppNotification | null>(null);

  const filteredNotifications = notifications.filter(n => {
    if (activeFilter === 'unread') return !n.read;
    if (activeFilter === 'payments') return n.type === 'NEW_PAYMENT' || n.type === 'payment_success' || n.type === 'NEW_TRANSFER';
    if (activeFilter === 'tickets') return n.type === 'SUPPORT_TICKET';
    return true;
  });

  const handleNotificationClick = async (notif: AppNotification) => {
    if (!notif.read) {
      await markNotificationAsRead(notif.id);
    }
    setIsOpen(false);
    setSelectedNotifForModal(notif);
  };

  const handleSingleMarkRead = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await markNotificationAsRead(id);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'NEW_PAYMENT':
      case 'payment_success':
        return <div className="p-2 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 rounded-xl"><CreditCardIcon className="h-5 w-5" /></div>;
      case 'NEW_TRANSFER':
        return <div className="p-2 bg-amber-100 dark:bg-amber-950/60 text-amber-600 rounded-xl"><BanknotesIcon className="h-5 w-5" /></div>;
      case 'NEW_REGISTRATION':
        return <div className="p-2 bg-blue-100 dark:bg-blue-950/60 text-blue-600 rounded-xl"><RocketLaunchIcon className="h-5 w-5" /></div>;
      case 'SUPPORT_TICKET':
        return <div className="p-2 bg-purple-100 dark:bg-purple-950/60 text-purple-600 rounded-xl"><TicketIcon className="h-5 w-5" /></div>;
      default:
        return <div className="p-2 bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 rounded-xl"><InformationCircleIcon className="h-5 w-5" /></div>;
    }
  };

  const formatNotificationTime = (createdAt: any) => {
    if (!createdAt) return 'Reciente';
    try {
      const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Hace un momento';
      if (diffMins < 60) return `Hace ${diffMins} min`;
      if (diffHours < 24) return `Hace ${diffHours} h`;
      if (diffDays === 1) return 'Ayer';
      return date.toLocaleDateString();
    } catch {
      return 'Reciente';
    }
  };

  return (
    <>
      <div className="relative inline-block text-left" ref={dropdownRef}>
        {/* Botón Campana */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 rounded-xl text-gray-500 dark:text-slate-300 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
          title="Centro de Notificaciones"
          aria-label="Centro de Notificaciones"
        >
          <BellIcon className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-extrabold text-white shadow-sm ring-2 ring-white animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown flotante */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-black/5 z-50 overflow-hidden border border-gray-100 dark:border-slate-800 transform origin-top-right transition-all">
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-slate-900 to-slate-800 text-white flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BellIcon className="h-5 w-5 text-blue-400" />
                <h3 className="font-bold text-sm">Notificaciones del Sistema</h3>
                {unreadCount > 0 && (
                  <span className="bg-rose-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                    {unreadCount} nuevas
                  </span>
                )}
              </div>

              <button
                onClick={() => markAllNotificationsAsRead()}
                className="text-xs text-blue-200 hover:text-white transition-colors flex items-center font-medium underline cursor-pointer"
                title="Marcar todas como leídas"
              >
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                Marcar leídas
              </button>
            </div>

            {/* Filtros */}
            <div className="flex border-b border-gray-100 dark:border-slate-800 bg-gray-50/80 dark:bg-slate-900 px-2 py-1.5 text-xs font-semibold overflow-x-auto space-x-1">
              {[
                { id: 'all', label: 'Todas' },
                { id: 'unread', label: `No leídas (${unreadCount})` },
                { id: 'payments', label: 'Pagos 💰' },
                { id: 'tickets', label: 'Soporte 🎫' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveFilter(tab.id as any)}
                  className={`px-2.5 py-1 rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                    activeFilter === tab.id
                      ? 'bg-white dark:bg-slate-800 text-blue-700 dark:text-blue-400 shadow-sm font-bold'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Lista de Notificaciones */}
            <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-100 dark:divide-slate-800">
              {filteredNotifications.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <CheckCircleIcon className="h-10 w-10 mx-auto text-gray-300 dark:text-slate-600 mb-2" />
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Todo al día</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">No hay notificaciones en esta categoría.</p>
                </div>
              ) : (
                filteredNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`p-3.5 flex items-start space-x-3 cursor-pointer transition-colors group ${
                      notif.read 
                        ? 'bg-white dark:bg-slate-900 hover:bg-gray-50/80 dark:hover:bg-slate-800/80' 
                        : 'bg-blue-50/40 dark:bg-blue-950/30 hover:bg-blue-50/80 dark:hover:bg-blue-900/40'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notif.type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-xs font-bold truncate ${notif.read ? 'text-gray-800 dark:text-slate-200' : 'text-blue-900 dark:text-blue-300'}`}>
                          {notif.title}
                        </p>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500 whitespace-nowrap ml-2">
                          {formatNotificationTime(notif.createdAt)}
                        </span>
                      </div>

                      <p className="text-xs text-gray-600 dark:text-slate-300 mt-1 line-clamp-2 leading-relaxed">
                        {notif.message}
                      </p>

                      {notif.details?.lubricentroName && (
                        <span className="inline-block mt-1.5 text-[10px] font-semibold text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                          {notif.details.lubricentroName}
                        </span>
                      )}
                    </div>

                    {!notif.read ? (
                      <button
                        type="button"
                        onClick={(e) => handleSingleMarkRead(e, notif.id)}
                        className="flex-shrink-0 self-center p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-full transition-colors"
                        title="Marcar como leída"
                      >
                        <span className="h-2.5 w-2.5 rounded-full bg-blue-600 block"></span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleSingleMarkRead(e, notif.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-green-600 rounded transition-opacity"
                        title="Leída"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-3 bg-gray-50 dark:bg-slate-800/80 border-t border-gray-100 dark:border-slate-800 text-center flex items-center justify-between px-4">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notificaciones');
                }}
                className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline transition-colors cursor-pointer"
              >
                📋 Ver todas las notificaciones →
              </button>

              <button
                onClick={() => markAllNotificationsAsRead()}
                className="text-xs font-bold text-gray-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-1 cursor-pointer"
              >
                <CheckCircleIcon className="h-3.5 w-3.5" />
                <span>Marcar todas leídas</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Lectura Completa de Notificación */}
      {selectedNotifForModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-slate-800">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center space-x-3">
                {getNotificationIcon(selectedNotifForModal.type)}
                <div>
                  <h3 className="font-bold text-base text-gray-900 dark:text-slate-100">{selectedNotifForModal.title}</h3>
                  <p className="text-xs text-gray-400">
                    {formatNotificationTime(selectedNotifForModal.createdAt)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedNotifForModal(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1 rounded-lg text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="my-4 p-4 bg-gray-50 dark:bg-slate-800 rounded-xl text-sm text-gray-800 dark:text-slate-200 leading-relaxed whitespace-pre-line border border-gray-100 dark:border-slate-700">
              {selectedNotifForModal.message}
            </div>

            {selectedNotifForModal.details && Object.keys(selectedNotifForModal.details).length > 0 && (
              <div className="mb-4 p-3 bg-blue-50/50 dark:bg-blue-950/40 rounded-xl border border-blue-100 dark:border-blue-800/40 text-xs space-y-1">
                <h4 className="font-bold text-blue-900 dark:text-blue-300 mb-1">Información Detallada:</h4>
                {Object.entries(selectedNotifForModal.details).map(([key, val]) => (
                  <div key={key} className="flex justify-between border-b border-blue-100/50 dark:border-blue-900/50 py-0.5">
                    <span className="font-semibold text-gray-600 dark:text-slate-400 capitalize">{key}:</span>
                    <span className="font-mono text-gray-900 dark:text-slate-100">{String(val)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-2 border-t border-gray-100 dark:border-slate-800">
              <button
                onClick={() => setSelectedNotifForModal(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-slate-200 rounded-xl hover:bg-gray-300 dark:hover:bg-slate-600 text-sm font-semibold cursor-pointer"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
